/**
 * ออกสัญญาจ้าง / สัญญาทดลองงาน — หน้าเต็มมี URL ของตัวเอง (/hr/contracts/new)
 *
 * ตามมาตรฐานบ้านนี้: ฟอร์มกรอกข้อมูลต้องเป็นหน้าเต็ม ไม่ใช่ modal — คนกรอกจะได้
 * ย้อนกลับได้ ส่งลิงก์ให้กันได้ และไม่โดนกล่องเล็ก ๆ บีบจนกรอกไม่ครบ
 *
 * ⭐ เลือกพนักงานด้วย <select> จริง ไม่ใช่ <datalist> — datalist ต้องพิมพ์ก่อน
 * ถึงจะขึ้น และบนมือถือแทบไม่โผล่เลย ลูกค้าจะแจ้งว่า "ระบบไม่มีข้อมูลให้เลือก"
 * ทั้งที่ข้อมูลครบ
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FileSignature, ArrowLeft } from 'lucide-react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useToast } from '../../components/ui/toast';
import { useEmployees, useCreateContract, type ContractKind } from '../../hooks';

interface EmpOption {
  emp_code: string;
  first_name?: string | null;
  last_name?: string | null;
  position?: string | null;
  status?: string | null;
  start_date?: string | null;
}

/** วันนี้ตามเวลาไทย (YYYY-MM-DD) — ใช้เป็นค่าเริ่มต้นของวันเริ่มสัญญา */
function todayThai(): string {
  return new Date(Date.now() + 7 * 3600_000).toISOString().slice(0, 10);
}

export function HRContractNew() {
  const navigate = useNavigate();
  const toast = useToast();
  const create = useCreateContract();
  const { data: empsRaw, isLoading: empsLoading } = useEmployees();

  const employees = useMemo<EmpOption[]>(() => {
    const raw = empsRaw as { employees?: EmpOption[] } | EmpOption[] | undefined;
    const list = Array.isArray(raw) ? raw : (raw?.employees ?? []);
    return list.filter((e) => e.status !== 'resigned' && e.status !== 'terminated');
  }, [empsRaw]);

  const [empCode, setEmpCode] = useState('');
  const [kind, setKind] = useState<ContractKind>('permanent');
  const [startDate, setStartDate] = useState(todayThai());
  const [endDate, setEndDate] = useState('');
  const [probationDays, setProbationDays] = useState('119');
  const [salary, setSalary] = useState('');
  const [workplace, setWorkplace] = useState('');
  const [reportTo, setReportTo] = useState('');
  const [renewalTerm, setRenewalTerm] = useState('');
  const [extra, setExtra] = useState('');
  const [duties, setDuties] = useState<string[]>(['', '', '']);

  const selected = employees.find((e) => e.emp_code === empCode);

  function setDuty(i: number, v: string) {
    setDuties((prev) => prev.map((d, idx) => (idx === i ? v : d)));
  }

  async function submit() {
    if (!empCode) { toast.error('กรุณาเลือกพนักงาน'); return; }
    if (!startDate) { toast.error('กรุณาระบุวันเริ่มสัญญา'); return; }
    if (kind === 'fixed_term' && !endDate) {
      toast.error('สัญญาแบบมีกำหนดเวลา ต้องระบุวันสิ้นสุดสัญญา');
      return;
    }
    if (endDate && endDate < startDate) {
      toast.error('วันสิ้นสุดสัญญาต้องไม่มาก่อนวันเริ่มสัญญา');
      return;
    }
    // ค่าจ้างเว้นว่างได้ = ให้ระบบดึงฐานเงินเดือนที่ตั้งไว้มาใช้
    const salaryNum = salary.trim() ? Number(salary.replace(/,/g, '')) : undefined;
    if (salaryNum != null && (!Number.isFinite(salaryNum) || salaryNum < 0)) {
      toast.error('อัตราค่าจ้างต้องเป็นตัวเลข');
      return;
    }

    try {
      const res = await create.mutateAsync({
        emp_code: empCode,
        contract_kind: kind,
        start_date: startDate,
        end_date: kind === 'fixed_term' ? endDate : undefined,
        probation_days: kind === 'probation' || probationDays.trim()
          ? Number(probationDays || 0) || undefined
          : undefined,
        salary_amount: salaryNum,
        fields: {
          workplace: workplace.trim() || undefined,
          report_to: reportTo.trim() || undefined,
          renewal_term: renewalTerm.trim() || undefined,
          extra_compensation: extra.trim() || undefined,
          duties: duties.map((d) => d.trim()).filter(Boolean),
        },
      });
      toast.success(`ออกสัญญาเลขที่ ${res.contract.doc_no} เป็นร่างแล้ว — ตรวจ PDF ก่อนส่งให้พนักงานเซ็น`);
      // ข้อมูลพนักงานไม่ครบ = สัญญามีช่องว่าง และแก้ทีหลังไม่ได้ ต้องบอกทันที
      if (res.missing_fields && res.missing_fields.length > 0) {
        toast.error(
          `⚠ ประวัติพนักงานยังไม่ครบ สัญญาจะเว้นว่าง: ${res.missing_fields.join(' · ')} — ` +
          'ควรกรอกในทะเบียนพนักงานแล้วยกเลิกออกใหม่ ก่อนส่งให้เซ็น',
        );
      }
      navigate('/hr/contracts');
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ออกสัญญาไม่สำเร็จ');
    }
  }

  return (
    <AdminLayout title="ออกสัญญาจ้าง">
      <div className="space-y-4 max-w-2xl">
        <button className="btn-secondary btn-slim" onClick={() => navigate('/hr/contracts')}>
          <ArrowLeft className="w-4 h-4" /> กลับไปรายการสัญญา
        </button>

        <div className="card p-4 space-y-3">
          <div className="font-semibold flex items-center gap-2">
            <FileSignature className="w-5 h-5 text-brand-600" /> ข้อมูลสัญญา
          </div>

          <div>
            <label className="label">พนักงาน</label>
            <select className="input w-full" value={empCode} onChange={(e) => setEmpCode(e.target.value)}>
              <option value="">{empsLoading ? 'กำลังโหลดรายชื่อ…' : '— เลือกพนักงาน —'}</option>
              {employees.map((e) => (
                <option key={e.emp_code} value={e.emp_code}>
                  {`${e.first_name ?? ''} ${e.last_name ?? ''}`.trim() || e.emp_code} ({e.emp_code})
                </option>
              ))}
            </select>
            {selected?.position && (
              <p className="text-xs text-gray-500 mt-1">
                ตำแหน่งในทะเบียน: {selected.position}
                {selected.start_date ? ` · เริ่มงาน ${selected.start_date}` : ''}
              </p>
            )}
          </div>

          <div>
            <label className="label">ประเภทสัญญา</label>
            <select className="input w-full" value={kind} onChange={(e) => setKind(e.target.value as ContractKind)}>
              <option value="permanent">ไม่มีกำหนดระยะเวลา (พนักงานประจำ)</option>
              <option value="fixed_term">มีกำหนดระยะเวลา (สัญญาจ้างตามระยะ)</option>
              <option value="probation">ทดลองงาน</option>
            </select>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">วันเริ่มสัญญา</label>
              <input type="date" className="input w-full" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </div>
            {kind === 'fixed_term' && (
              <div>
                <label className="label">วันสิ้นสุดสัญญา</label>
                <input type="date" className="input w-full" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            )}
            <div>
              <label className="label">ช่วงทดลองงาน (วัน)</label>
              <input
                type="number" inputMode="numeric" className="input w-full"
                value={probationDays} onChange={(e) => setProbationDays(e.target.value)}
                placeholder="เว้นว่าง = ไม่มีทดลองงาน"
              />
              <p className="text-xs text-gray-500 mt-1">นิยมใช้ 119 วัน (ไม่ถึง 120 วันตามกฎหมายค่าชดเชย)</p>
            </div>
            <div>
              <label className="label">อัตราค่าจ้าง (บาท)</label>
              <input
                type="text" inputMode="decimal" className="input w-full"
                value={salary} onChange={(e) => setSalary(e.target.value)}
                placeholder="เว้นว่าง = ใช้ฐานเงินเดือนที่ตั้งไว้"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="label">สถานที่ทำงาน</label>
              <input className="input w-full" value={workplace} onChange={(e) => setWorkplace(e.target.value)} />
            </div>
            <div>
              <label className="label">รายงานตรงต่อ</label>
              <input className="input w-full" value={reportTo} onChange={(e) => setReportTo(e.target.value)} />
            </div>
          </div>

          <div>
            <label className="label">เงื่อนไขต่อสัญญา</label>
            <input
              className="input w-full" value={renewalTerm} onChange={(e) => setRenewalTerm(e.target.value)}
              placeholder="เช่น พิจารณาต่อสัญญาตามผลงาน"
            />
          </div>

          <div>
            <label className="label">ค่าตอบแทนเพิ่มเติม</label>
            <input
              className="input w-full" value={extra} onChange={(e) => setExtra(e.target.value)}
              placeholder="เช่น ค่าคอมมิชชั่นตามนโยบายบริษัท · เว้นว่าง = ไม่มี"
            />
          </div>

          <div>
            <label className="label">หน้าที่ความรับผิดชอบ</label>
            {duties.map((d, i) => (
              <input
                key={i}
                className="input w-full mb-2"
                value={d}
                onChange={(e) => setDuty(i, e.target.value)}
                placeholder={`ข้อ ${i + 1}`}
              />
            ))}
            {duties.length < 6 && (
              <button className="btn-secondary btn-slim" onClick={() => setDuties((p) => [...p, ''])}>
                เพิ่มอีกข้อ
              </button>
            )}
          </div>
        </div>

        <div className="card p-4 text-sm text-gray-600">
          ข้อตกลง 25 ข้อ (ส่วนที่ 2 ของแบบฟอร์ม FM-HR-001) จะถูกแนบให้อัตโนมัติและแก้ไม่ได้ —
          สัญญาที่ออกจะเก็บสำเนาข้อความไว้กับตัวเอง แม้แม่แบบจะถูกแก้ในอนาคต ฉบับที่เซ็นแล้วจะไม่เปลี่ยนตาม
        </div>

        <button className="btn-cta w-full py-3" onClick={() => void submit()} disabled={create.isPending}>
          {create.isPending ? 'กำลังออกสัญญา…' : 'ออกสัญญา (เป็นร่าง)'}
        </button>
        <p className="text-xs text-gray-500 text-center">
          ออกแล้วยังแก้ไม่ได้ทันที — ตรวจ PDF ก่อน แล้วค่อยกด "ส่งให้ลงนาม" ในหน้ารายการ
        </p>
      </div>
    </AdminLayout>
  );
}
